import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Save, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { api, type Lieferart, type IdP, type WelcomeMail } from "@/lib/api";
import { errText } from "@/lib/utils";

const idpLabel = (p: IdP) => (p === "entra" ? "Entra" : "Moodle (lokal)");

const blankLieferart = (): Lieferart => ({
  edu: "",
  name: "",
  identity_provider: "entra",
  kurs_vorlauf_tage: 14,
  teilnehmer_vorlauf_tage: 7,
  trainer_vorlauf_tage: 21,
  fruehestes_start: "",
});

export function SettingsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl tracking-tight text-foreground">Einstellungen</h1>
        <p className="text-sm text-muted-foreground">
          Lieferarten (welche Kurse, ab wann) und der Text der Willkommens-Mail.
        </p>
      </div>
      <LieferartEditor />
      <WelcomeMailEditor />
    </div>
  );
}

function LieferartEditor() {
  const [rows, setRows] = useState<Lieferart[]>([]);
  const [editing, setEditing] = useState<Lieferart | null>(null);
  const [isNew, setIsNew] = useState(false);

  async function reload() {
    try {
      setRows(await api.getLieferarten());
    } catch (err) {
      toast.error("Lieferarten laden fehlgeschlagen", { description: errText(err) });
    }
  }
  useEffect(() => {
    reload();
  }, []);

  // persist schreibt die komplette Liste und übernimmt die Server-Antwort.
  async function persist(next: Lieferart[]) {
    const saved = await api.putLieferarten(next);
    setRows(saved);
  }

  async function remove(edu: string) {
    if (!confirm(`Lieferart "${edu}" wirklich löschen?`)) return;
    try {
      await persist(rows.filter((r) => r.edu !== edu));
      toast.success("Lieferart gelöscht");
    } catch (err) {
      toast.error("Löschen fehlgeschlagen", { description: errText(err) });
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Lieferarten</CardTitle>
            <Button
              size="sm"
              onClick={() => {
                setEditing(blankLieferart());
                setIsNew(true);
              }}
            >
              <Plus className="h-3.5 w-3.5" /> Lieferart
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Lieferart angelegt.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">EDU</th>
                    <th className="py-2 pr-4 font-medium">Name</th>
                    <th className="py-2 pr-4 font-medium">Identity Provider</th>
                    <th className="py-2 pr-4 font-medium">Vorlauf K/T/Tr (Tage)</th>
                    <th className="py-2 pr-4 font-medium">ab</th>
                    <th className="py-2 pr-2 font-medium text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.edu} className="border-t border-border">
                      <td className="py-2 pr-4 font-mono">{r.edu}</td>
                      <td className="py-2 pr-4">{r.name || "—"}</td>
                      <td className="py-2 pr-4">{idpLabel(r.identity_provider)}</td>
                      <td className="py-2 pr-4">
                        {r.kurs_vorlauf_tage} / {r.teilnehmer_vorlauf_tage} / {r.trainer_vorlauf_tage}
                      </td>
                      <td className="py-2 pr-4">{r.fruehestes_start || "—"}</td>
                      <td className="py-2 pr-2">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Bearbeiten"
                            onClick={() => {
                              setEditing({ ...r });
                              setIsNew(false);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" aria-label="Löschen" onClick={() => remove(r.edu)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <LieferartDialog
          value={editing}
          isNew={isNew}
          existing={rows}
          onClose={() => setEditing(null)}
          onSave={async (ls) => {
            const next = isNew
              ? [...rows, ls]
              : rows.map((r) => (r.edu === ls.edu ? ls : r));
            await persist(next);
            setEditing(null);
            toast.success("Lieferart gespeichert");
          }}
        />
      )}
    </>
  );
}

function LieferartDialog({
  value,
  isNew,
  existing,
  onClose,
  onSave,
}: {
  value: Lieferart;
  isNew: boolean;
  existing: Lieferart[];
  onClose: () => void;
  onSave: (ls: Lieferart) => Promise<void>;
}) {
  const [draft, setDraft] = useState<Lieferart>(value);
  const [saving, setSaving] = useState(false);
  const set = (p: Partial<Lieferart>) => setDraft((d) => ({ ...d, ...p }));
  const num = (v: string) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  };

  async function save() {
    const edu = draft.edu.trim();
    if (edu === "") {
      toast.error("EDU darf nicht leer sein");
      return;
    }
    if (isNew && existing.some((r) => r.edu === edu)) {
      toast.error(`Lieferart "${edu}" existiert bereits`);
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...draft, edu });
    } catch (err) {
      toast.error("Speichern fehlgeschlagen", { description: errText(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isNew ? "Neue Lieferart" : `Lieferart ${draft.edu} bearbeiten`}</DialogTitle>
          <DialogDescription>
            Welche Kurse dieser Lieferart synchronisiert werden und ab wann.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          <Field label="EDU (Lieferart-Code)">
            <Input
              value={draft.edu}
              maxLength={2}
              disabled={!isNew}
              placeholder="MO"
              onChange={(e) => set({ edu: e.target.value })}
            />
          </Field>
          <Field label="Name">
            <Input value={draft.name ?? ""} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Identity Provider">
            <select
              className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
              value={draft.identity_provider}
              onChange={(e) => set({ identity_provider: e.target.value as IdP })}
            >
              <option value="entra">Entra</option>
              <option value="moodle_local">Moodle (lokal)</option>
            </select>
          </Field>
          <Field label="Frühestes Startdatum">
            <Input
              type="date"
              value={draft.fruehestes_start}
              onChange={(e) => set({ fruehestes_start: e.target.value })}
            />
          </Field>
          <Field label="Kurs-Vorlauf (Tage)">
            <Input
              type="number"
              value={draft.kurs_vorlauf_tage}
              onChange={(e) => set({ kurs_vorlauf_tage: num(e.target.value) })}
            />
          </Field>
          <Field label="Teilnehmer-Vorlauf (Tage)">
            <Input
              type="number"
              value={draft.teilnehmer_vorlauf_tage}
              onChange={(e) => set({ teilnehmer_vorlauf_tage: num(e.target.value) })}
            />
          </Field>
          <Field label="Trainer-Vorlauf (Tage)">
            <Input
              type="number"
              value={draft.trainer_vorlauf_tage}
              onChange={(e) => set({ trainer_vorlauf_tage: num(e.target.value) })}
            />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WelcomeMailEditor() {
  const [tmpl, setTmpl] = useState<WelcomeMail>({ subject: "", body: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getWelcomeMail().then(setTmpl).catch((err) => toast.error("Mail-Template laden", { description: errText(err) }));
  }, []);

  async function save() {
    setSaving(true);
    try {
      setTmpl(await api.putWelcomeMail(tmpl));
      toast.success("Welcome-Mail gespeichert");
    } catch (err) {
      toast.error("Speichern fehlgeschlagen", { description: errText(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Willkommens-Mail</CardTitle>
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Speichern
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field label="Betreff">
          <Input value={tmpl.subject} onChange={(e) => setTmpl({ ...tmpl, subject: e.target.value })} />
        </Field>
        <Field label="Text">
          <textarea
            className="min-h-48 w-full rounded-md border border-input bg-transparent p-3 font-mono text-sm"
            value={tmpl.body}
            onChange={(e) => setTmpl({ ...tmpl, body: e.target.value })}
          />
        </Field>
        <p className="text-xs text-muted-foreground">
          Variablen: <code>{"{{.Vorname}}"}</code> <code>{"{{.Nachname}}"}</code> <code>{"{{.UPN}}"}</code>{" "}
          <code>{"{{.Email}}"}</code> <code>{"{{.Passwort}}"}</code>
        </p>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
